import {
  AddPartnerDialog,
  EditPartnerDialog,
  LinkExistingPartnerDialog,
} from '@/features/add-partner';
import { CirclePlay } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  ClientDataModal,
  ClientDuplicationDialog,
  DeleteClientDialog,
  ERROR_OR_INCOMPLETE_STATUS_TAGS,
  SendResultsLinkModal,
  cn,
  fetchClientsForAdvisorByAdvisorIdRequest,
  updateClientsSearchText,
} from '..';
import { PlayIcon } from '../components/PlayIcon';
import {
  selectAdvisorHierarchyState,
  selectClients,
  selectClientsIsLoading,
  selectClientsSearchText,
  selectSessionAdvisor,
} from '../model/selectors';

import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import { DashboardCardView } from '@/components/advisor-mobile-dashboard';
import { useIsEdjPilotAdvisor } from '@/shared/hooks/use-edj-pilot-advisor';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { DateRangeFilter, type DateRange } from '@client/features/advisor-dashboard';
import { SampleClientsToggle } from '@/shared/components/sample-clients-toggle';
import { SampleClientsAlertDialog } from '@/shared/components/sample-clients-alert-dialog';
import { advisorsApi } from '@/api/waterlily';
import { fetchSessionForAuthRequest } from '@/model/sessionModel';
import { useMutation } from '@tanstack/react-query';
import { useInviteClientTrigger } from '@client/features/invite-client';
import {
  CollaborateOnCaseDialog,
  ShareClientWithSupervisorDialog,
} from '@client/features/share-client';
import { PolicyDetailsDialog } from '@/features/genworth/policy-details/policy-details-dialog';
import { TrialProgress } from '@client/features/trial-mode';
import { useIsMobile } from '@client/hooks/use-mobile';
import { useTrialProgressSync } from '@client/hooks/useTrialProgressSync';
import { ColumnDef } from '@tanstack/react-table';
import { Send, FileUser } from 'lucide-react';
import { DataTable } from '../components/DataTable';
import { createClientsTableColumns } from '@/features/clients-table';
import { QuickPlanDialog } from '@client/features/client-quick-plan';
import { Button } from '../components/ui/button';
import { DEFAULT_DASHBOARD_CONFIG, type DashboardConfig } from '@/components/dashboard-config';

type ModalType =
  | 'delete'
  | 'linkPartner'
  | 'addPartner'
  | 'editPartner'
  | 'sendClientSigninLink'
  | 'clientData'
  | 'clientDataEdit'
  | 'edjQuickEstimateEdit'
  | 'newDuplicateClient'
  | 'updateIntakeForm'
  | 'shareWithSupervisor'
  | 'editPolicyDetails'
  | null;
export interface ModalState {
  type: ModalType;
  client?: Client; // Could be typed more specifically based on your needs
}

const CLIENTS_VIDEO_URL = 'https://www.loom.com/embed/90255565dc404610846adecbd7d82049';

interface AdvisorDashboardClientsProps {
  config?: DashboardConfig['clients'];
}

export function AdvisorDashboardClients({
  config = DEFAULT_DASHBOARD_CONFIG.clients,
}: AdvisorDashboardClientsProps = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const isEdjPilot = useIsEdjPilotAdvisor();
  const clients = useSelector(selectClients);
  const clientsIsLoading = useSelector(selectClientsIsLoading);
  const { subordinateAdvisors } = useSelector(selectAdvisorHierarchyState);
  const [searchText, setSearchText] = useState(useSelector(selectClientsSearchText));
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const sampleClientsKey = `showSampleClients_${advisor?.advisorId}`;
  const [showSampleClients, setShowSampleClients] = useState(() => {
    const stored = localStorage.getItem(sampleClientsKey);
    return stored !== null ? stored === 'true' : true;
  });
  const handleToggleSampleClients = useCallback(
    (show: boolean) => {
      setShowSampleClients(show);
      localStorage.setItem(sampleClientsKey, String(show));
    },
    [sampleClientsKey],
  );
  const { setInviteClientParams } = useInviteClientTrigger();

  const hasSampleClients = useMemo(
    () => clients.some(client => client.isSampleClient && client.advisorId === advisor?.advisorId),
    [clients, advisor?.advisorId],
  );

  const [sampleAlertDismissed, setSampleAlertDismissed] = useState(false);
  const showSampleClientsAlert =
    !sampleAlertDismissed && advisor?.metadata?.showSampleClientsAlert === true;

  const dismissSampleAlertMutation = useMutation({
    mutationFn: () => {
      if (!advisor) throw new Error('No advisor');
      return advisorsApi.putById(advisor.advisorId, {
        ...advisor,
        metadata: { ...(advisor.metadata || {}), showSampleClientsAlert: false },
      });
    },
    onSuccess: () => {
      setSampleAlertDismissed(true);
      dispatch(fetchSessionForAuthRequest({ invalidating: true }));
    },
  });

  const handleDismissSampleClientsAlert = useCallback(() => {
    dismissSampleAlertMutation.mutate();
  }, [dismissSampleAlertMutation]);

  const { isStepCompleted, shouldShowQuickStartGuide } = useTrialProgressSync(
    advisor?.advisorId ?? '',
    advisor?.advisorCreatedDateTime,
  );

  const filteredClients = useMemo(() => {
    let result = clients.filter(
      client => !client.isSampleClient || client.advisorId === advisor?.advisorId,
    );
    if (!showSampleClients) {
      result = result.filter(client => !client.isSampleClient);
    }
    if (dateRange) {
      result = result.filter(client => {
        const addedDate = new Date(client.clientAddedDateTime);
        return addedDate >= dateRange.from && addedDate <= dateRange.to;
      });
    }
    return result;
  }, [clients, dateRange, showSampleClients, advisor?.advisorId]);

  const firstClientWithCompletedIntake = useMemo(
    () =>
      clients.find(
        client =>
          !client.isSampleClient &&
          !client.clientTags.some(tag => ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(tag)),
      ),
    [clients],
  );

  const handleReviewResults = useCallback(() => {
    if (firstClientWithCompletedIntake) {
      navigate(`/clients/${firstClientWithCompletedIntake.clientId}/onboarding/results-are-in`);
    }
  }, [firstClientWithCompletedIntake, navigate]);

  // Wait for both clients and subordinateAdvisors to load before rendering the table
  // subordinateAdvisors is null during loading, then becomes an array (even if empty) once loaded
  const isSubordinateAdvisorsLoading = subordinateAdvisors === null;
  const isLoadingData = clientsIsLoading || isSubordinateAdvisorsLoading;

  // Hide the clients table when data is fetched and there are no clients
  const hasNoClients = !isLoadingData && clients.length === 0;

  const handleSearchTextChange = (searchText: string) => {
    setSearchText(searchText);
    dispatch(updateClientsSearchText(searchText));
    localStorage.setItem('clientsSearchText', searchText);
  };

  const [modalState, setModalState] = useState<ModalState>({ type: null });
  const isMobile = useIsMobile();
  const onCloseModal = () => setModalState({ type: null });
  const { flags } = useFeatureFlags();

  const [quickPlanOpen, setQuickPlanOpen] = useState(false);

  const handleQuickPlanSuccess = useCallback(() => {
    setQuickPlanOpen(false);
    dispatch(
      fetchClientsForAdvisorByAdvisorIdRequest({
        advisorId: advisor.advisorId,
        isInvalidation: false,
      }),
    );
  }, [dispatch, advisor.advisorId]);

  const memoizedColumns = useMemo(
    () =>
      createClientsTableColumns({
        data: clients,
        setModalState,
        sessionAdvisor: advisor,
        subordinateAdvisors: subordinateAdvisors || ([] as Advisor[]),
        isEdjPilot,
        hiddenColumnIds: config.hiddenColumnIds,
        showExploreQuotes: config.showExploreQuotes,
      }),
    [
      clients,
      setModalState,
      advisor,
      subordinateAdvisors,
      isEdjPilot,
      config.hiddenColumnIds,
      config.showExploreQuotes,
    ],
  );

  const useQuickStartAction = config.headerPrimaryAction === 'quickStart';

  const quickStartButton = (
    <Button
      size="lg"
      data-testid="new-case-quick-start-button"
      onClick={() => setQuickPlanOpen(true)}
      className="bg-primary hover:bg-primary-hover rounded-full py-6 text-base text-white"
    >
      <FileUser className="h-6 w-6" />
      New Case Quick Start
    </Button>
  );

  const memoizedHandleRefresh = useCallback(() => {
    dispatch(
      fetchClientsForAdvisorByAdvisorIdRequest({
        advisorId: advisor.advisorId,
        isInvalidation: false,
      }),
    );
  }, [advisor.advisorId, dispatch]);

  // Memoize the entire DataTable instance
  const memoizedDataTable = useMemo(
    () =>
      isSubordinateAdvisorsLoading ? null : (
        <DataTable<Client>
          columns={memoizedColumns as ColumnDef<Client>[]}
          getRowId={row => row.clientId}
          data={filteredClients}
          isLoading={isLoadingData}
          globalSearchFilterKey="Client"
          globalSearchText={searchText ?? undefined}
          globalSearchFieldKeys={['clientName', 'email']}
          handleRefresh={memoizedHandleRefresh}
          className="mb-12"
          tableId="clientsTable"
          variant="outlined"
          initialColumnVisibility={{
            advisor: Boolean(subordinateAdvisors && subordinateAdvisors.length > 0),
            organization:
              !isEdjPilot && Boolean(subordinateAdvisors && subordinateAdvisors.length > 0),
            email: false,
          }}
          initialColumnPinning={{
            left: ['clientName'],
            right: [],
          }}
          handleSearchTextChange={handleSearchTextChange}
          tableSkeletonRows={10}
          initialSorting={[{ id: 'addedDate', desc: true }]}
          headerExtras={
            <div className="flex items-center gap-2">
              {hasSampleClients && (
                <SampleClientsToggle
                  showSampleClients={showSampleClients}
                  onToggle={handleToggleSampleClients}
                />
              )}
              <DateRangeFilter
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
              />
            </div>
          }
        />
      ),
    [
      memoizedColumns,
      filteredClients,
      isLoadingData,
      searchText,
      memoizedHandleRefresh,
      subordinateAdvisors,
      dateRange,
      hasSampleClients,
      showSampleClients,
      isEdjPilot,
    ],
  );

  return (
    <div className="h-full">
      {flags.mobileDashboardRedesign ? (
        <main className={cn(isMobile ? 'px-0 pt-2' : 'px-4 pb-12 pt-3 sm:px-6 lg:px-8')}>
          {!isMobile ? (
            <DashboardPageHeader
              title="Clients"
              videoPlayer={
                <PlayIcon
                  id="clients-tab-demo-video"
                  icon={CirclePlay}
                  videoUrl={CLIENTS_VIDEO_URL}
                  size={28}
                />
              }
              actions={
                useQuickStartAction ? (
                  quickStartButton
                ) : (
                  <Button
                    size="lg"
                    data-testid="invite-client-button-test"
                    onClick={() => setInviteClientParams()}
                    className="bg-primary hover:bg-primary-hover rounded-full py-6 text-base text-white"
                  >
                    <Send className="h-6 w-6" />
                    Invite Clients
                  </Button>
                )
              }
            />
          ) : (
            <div className="mb-2 flex items-center justify-between px-4">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-gray-900">Clients</h1>
                <PlayIcon
                  id="clients-tab-demo-video"
                  icon={CirclePlay}
                  videoUrl={CLIENTS_VIDEO_URL}
                  size={28}
                />
              </div>
            </div>
          )}

          <div className={isMobile ? '' : 'my-4'}>
            {!isMobile && shouldShowQuickStartGuide && (
              <div className="mb-6">
                <TrialProgress
                  onStartInvite={
                    useQuickStartAction
                      ? () => setQuickPlanOpen(true)
                      : () => setInviteClientParams()
                  }
                  onReviewResults={handleReviewResults}
                  hasInvitedClient={isStepCompleted('invite-client')}
                  hasCompletedIntakeForm={
                    isStepCompleted('client-intake-form') && Boolean(firstClientWithCompletedIntake)
                  }
                  hasCompletedReview={isStepCompleted('review-results')}
                />
              </div>
            )}
            {!hasNoClients &&
              (isMobile ? (
                <DashboardCardView
                  clients={filteredClients}
                  setModalState={setModalState}
                  sessionAdvisor={advisor}
                  subordinateAdvisors={subordinateAdvisors || []}
                  isLoading={isLoadingData}
                  handleRefresh={memoizedHandleRefresh}
                  globalSearchText={searchText ?? undefined}
                  handleSearchTextChange={handleSearchTextChange}
                  dateRange={dateRange}
                  onDateRangeChange={setDateRange}
                  hasSampleClients={hasSampleClients}
                  showSampleClients={showSampleClients}
                  onToggleSampleClients={handleToggleSampleClients}
                  hiddenColumnIds={config.hiddenColumnIds}
                />
              ) : (
                memoizedDataTable
              ))}
          </div>
        </main>
      ) : (
        <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
          <DashboardPageHeader
            title="Clients"
            videoPlayer={
              <PlayIcon
                id="clients-tab-demo-video"
                icon={CirclePlay}
                videoUrl={CLIENTS_VIDEO_URL}
                size={28}
              />
            }
            actions={
              useQuickStartAction ? (
                quickStartButton
              ) : (
                <Button
                  size="lg"
                  data-testid="invite-client-button-test"
                  onClick={() => setInviteClientParams()}
                  className="bg-primary hover:bg-primary-hover rounded-full py-6 text-base text-white"
                >
                  <Send className="h-7 w-7" />
                  Add Clients
                </Button>
              )
            }
          />
          {!hasNoClients && memoizedDataTable}
        </main>
      )}
      {modalState.client && (
        <>
          <DeleteClientDialog
            client={modalState.client}
            open={modalState.type === 'delete'}
            onClose={onCloseModal}
          />
          <LinkExistingPartnerDialog
            client={modalState.client}
            open={modalState.type === 'linkPartner'}
            onClose={onCloseModal}
          />
          <AddPartnerDialog
            open={modalState.type === 'addPartner'}
            onOpenChange={isOpen => {
              if (!isOpen) onCloseModal();
            }}
            clientId={modalState.client.clientId}
            onSubmitSuccess={onCloseModal}
          />
          {(() => {
            const partner =
              modalState.client!.mutableClientPartner ??
              clients.find(c => c.clientId === modalState.client!.partnerClientId);
            return partner ? (
              <EditPartnerDialog
                open={modalState.type === 'editPartner'}
                onOpenChange={isOpen => {
                  if (!isOpen) onCloseModal();
                }}
                client={modalState.client!}
                partner={partner}
              />
            ) : null;
          })()}
          <SendResultsLinkModal
            client={modalState.client}
            open={modalState.type === 'sendClientSigninLink'}
            onClose={onCloseModal}
            hideSendButton={isEdjPilot}
          />
          <ClientDataModal
            partialClient={modalState.client}
            open={
              modalState.type === 'clientData' ||
              modalState.type === 'clientDataEdit' ||
              modalState.type === 'edjQuickEstimateEdit'
            }
            initialMode={
              modalState.type === 'clientDataEdit' || modalState.type === 'edjQuickEstimateEdit'
                ? 'edit'
                : 'view'
            }
            quickEstimateOnly={modalState.type === 'edjQuickEstimateEdit'}
            onClose={onCloseModal}
          />
          <ClientDuplicationDialog
            client={modalState.client}
            open={modalState.type === 'newDuplicateClient'}
            onClose={onCloseModal}
          />
          {(() => {
            const shareDialogProps = {
              client: modalState.client,
              partner:
                modalState.client.mutableClientPartner ??
                (modalState.client.partnerClientId
                  ? (clients.find(c => c.clientId === modalState.client!.partnerClientId) ?? null)
                  : null),
              open: modalState.type === 'shareWithSupervisor',
              onClose: onCloseModal,
            };

            // Edward Jones advisors get the collaborate framing: no note step, and an explicit
            // acknowledgement of the data share before it happens.
            return isEdjPilot ? (
              <CollaborateOnCaseDialog {...shareDialogProps} />
            ) : (
              <ShareClientWithSupervisorDialog {...shareDialogProps} />
            );
          })()}
          <PolicyDetailsDialog
            client={modalState.client}
            open={modalState.type === 'editPolicyDetails'}
            onClose={onCloseModal}
          />
        </>
      )}
      {useQuickStartAction && (
        <QuickPlanDialog
          open={quickPlanOpen}
          onOpenChange={setQuickPlanOpen}
          advisorId={advisor.advisorId}
          onSubmitSuccess={handleQuickPlanSuccess}
          initialMode="quick-plan"
          lockMode
        />
      )}
      <SampleClientsAlertDialog
        open={showSampleClientsAlert || dismissSampleAlertMutation.isPending}
        loading={dismissSampleAlertMutation.isPending}
        onAcknowledge={handleDismissSampleClientsAlert}
      />
    </div>
  );
}
